import styled from 'styled-components';

import Button from '~/src/common/components/Button';
import { ButtonContent, InnerButton } from '~/src/common/components/Button/layout';
import { getMediaQuery } from '~/src/common/utils/style';
import ImageSlider from '~/src/screens/CatalogContent/ContentScreen/ImageSlider/ImageSlider';
import { GridContent as BaseGridContent } from '~/src/screens/CatalogContent/ContentScreen/layout';
import { breakpoints, media } from '~/src/styles-constants';

const GRID_AREA_HEADER = 'header';
const GRID_AREA_SLIDE = 'slide';
const GRID_AREA_BREADCRUMBS = 'breadcrumbs';
const GRID_AREA_BASIC_INGREDIENTS = 'basic-ingredients';
export const GRID_AREA_INGREDIENTS = 'ingredients';
const GRID_AREA_STEPS = 'steps';
const GRID_AREA_DESCRIPTION = 'description';
const GRID_AREA_SHORT_DESCRIPTION = 'short-description';

export const GridContent = styled(BaseGridContent)<{ $hasShortDescription: boolean }>`
  grid-template-areas:
    '${GRID_AREA_HEADER}'
    '${GRID_AREA_HEADER}'
    '${GRID_AREA_SLIDE}'
    '${GRID_AREA_BASIC_INGREDIENTS}'
    '${GRID_AREA_INGREDIENTS}'
    ${({ $hasShortDescription }) =>
      $hasShortDescription ? `'${GRID_AREA_SHORT_DESCRIPTION}'` : undefined}
    '${GRID_AREA_STEPS}'
    '${GRID_AREA_DESCRIPTION}';

  ${media.M<{ $hasShortDescription: boolean }>`
    grid-template-areas:
    "${GRID_AREA_BREADCRUMBS} ${GRID_AREA_HEADER}"
    "${GRID_AREA_SLIDE} ${GRID_AREA_HEADER}"
    "${GRID_AREA_BASIC_INGREDIENTS} ${GRID_AREA_HEADER}"
    "${GRID_AREA_INGREDIENTS} ${GRID_AREA_HEADER}"
    ${({ $hasShortDescription }) =>
      $hasShortDescription ? `"${GRID_AREA_SHORT_DESCRIPTION} ${GRID_AREA_HEADER}"` : ''}
    "${GRID_AREA_STEPS} ${GRID_AREA_HEADER}"
    "${GRID_AREA_DESCRIPTION} ${GRID_AREA_HEADER}";
  `}
`;

export const Slider = styled(ImageSlider)`
  padding: 0 8px;
`;

export const IngredientsSectionsContainer = styled.div`
  grid-area: ${GRID_AREA_INGREDIENTS};
  min-width: 0;
`;

export const ProductHeaderPaneContainer = styled.div`
  grid-area: ${GRID_AREA_HEADER};
  grid-column: 1;
  grid-row: 2;

  @media ${getMediaQuery(0, breakpoints.M)} {
    overflow: auto;
  }

  ${media.M`
    grid-row: 1 / 6;
    grid-column: 2;
    margin-bottom: 88px;
  `}
`;

export const InformationRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  text-align: center;

  & > div {
    width: 70px;

    p {
      margin: 2px 0 0;
    }

    p:first-of-type {
      font-size: 12px;
      line-height: 16px;
    }

    p:last-of-type {
      font-size: 16px;
      line-height: 16px;
      font-weight: 500;
    }
  }
`;

export const SectionName = styled.h2`
  font-size: 20px;
  line-height: 28px;
  font-weight: 500;
  margin: 0 0 8px;
`;

export const IngredientsSection = styled.section`
  grid-area: ${GRID_AREA_BASIC_INGREDIENTS};
  padding: 0 16px;

  ${media.M`
    padding: 0;
    margin-left: 16px;
  `}
`;

export const IngredientsContainer = styled.div`
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.palette.common.LIGHT_GREEN};

  & > p:first-child {
    padding-bottom: 16px;
    border-bottom: solid 1px ${({ theme }) => theme.palette.common.LIGHT_GREEN};

    & b {
      font-weight: 500;
    }
  }
`;

export const IngredientsInnerContainer = styled.dl`
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 16px;

  ${media.M`
    grid-template-columns: repeat(2, auto 1fr);
  `}

  dd {
    font-size: 14px;
    line-height: 20px;
    margin: 0;
  }

  dt {
    font-weight: 500;
  }
`;

export const IngredientCardImage = styled.img`
  height: 40px;
  width: 32px;
  object-fit: cover;
  border-radius: 4px;
`;

export const IngredientGenericContainer = styled.div`
  height: 40px;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  padding: 0 6px;

  border-radius: 4px;
  background-color: ${({ theme }) => theme.palette.common.XLIGHT_GREEN};
`;

export const IngredientInformations = styled.div`
  margin-left: 8px;
  overflow: hidden;

  & > p {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    margin: 0;
  }

  & > p:first-child {
    font-size: 14px;
    line-height: 18px;
    font-weight: 500;
  }

  & > p:last-child:not(:first-child) {
    font-size: 14px;
    line-height: 16px;
  }
`;

export const ShortDescription = styled.div`
  grid-area: ${GRID_AREA_SHORT_DESCRIPTION};
  padding: 0 16px;

  ${media.M`
    padding: 0;
    margin-left: 16px;
  `}
`;

export const StepsSectionHeader = styled.div`
  & > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 8px;

    ${media.M`
      justify-content: flex-start;
      margin-top: 0px;
    `}
  }

  ${media.M`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `}
`;

export const StepContainer = styled.div`
  padding: 16px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.palette.common.LIGHT_GREEN};

  &:not(:last-child) {
    margin-bottom: 24px;
  }
`;

export const StepNumber = styled.p`
  margin: 0 0 8px;
  font-family: ${({ theme }) => theme.fonts.secondary};
  color: ${({ theme }) => theme.palette.common.PRIMARY};
  font-size: 28px;
  line-height: 32px;
  font-weight: 700;
`;

export const IngredientsList = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 24px;
  margin-top: 16px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.palette.common.LIGHT_GREEN};

  & > div {
    overflow: hidden;
    display: flex;
    align-items: center;
  }
`;

export const StepsSection = styled.section`
  grid-area: ${GRID_AREA_STEPS};
  margin-top: 8px;
  padding: 0 16px;

  ${media.M`
    padding: 0;
    margin-left: 16px;
  `}
`;

export const DescriptionContainer = styled.div`
  grid-area: ${GRID_AREA_DESCRIPTION};
  padding: 0;

  ${media.M`
    margin-left: 16px;
  `}
`;

export const DescriptionContentContainer = styled.div`
  padding: 16px;
  margin: 0 16px 40px;

  ${media.M`
    margin: 0 0 88px 0;
  `}

  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.palette.common.LIGHT_GREEN};

  & > div {
    margin-top: 16px;
    padding-top: 24px;
    border-top: 1px solid ${({ theme }) => theme.palette.common.LIGHT_GREEN};
  }

  & > h2 {
    font-size: 20px;
    line-height: 24px;
    margin: 0;
    font-weight: 500;
  }
`;

export const AddToCartContainer = styled.div`
  z-index: 10;
  position: fixed;
  inset: auto 0 0;
  background-color: ${({ theme }) => theme.palette.background};

  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 8px calc(env(safe-area-inset-bottom) + 16px) 8px;

  border-top: 1px solid ${({ theme }) => theme.palette.common.BACKGROUND_3};

  ${media.M`
    position: initial;

    border-bottom: 1px solid ${({ theme }) => theme.palette.common.BACKGROUND_3};

    padding: 16px;
  `}
`;

export const PortionsText = styled.p`
  margin: 0 8px;

  & > span:first-child {
    font-size: 16px;
    line-height: 16px;
    font-weight: 500;
  }

  & > span:last-child {
    margin-left: 4px;
    font-size: 12px;
    line-height: 16px;
  }
`;

export const AddToCartButton = styled(Button)`
  height: 40px;

  ${InnerButton} > ${ButtonContent} {
    padding: 0 8px;
  }
`;

export const UnavailableProduct = styled.p`
  font-size: 10px;
  line-height: 12px;
  color: ${({ theme }) => theme.palette.common.DISABLED};
`;
